import { IconBold, IconItalic, IconList, IconListNumbers, IconUnderline } from '@tabler/icons-react';
import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
    $getSelection,
    $isRangeSelection,
    FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {
    INSERT_UNORDERED_LIST_COMMAND,
    INSERT_ORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
    $insertList,
    $removeList,
} from '@lexical/list';
import NSButton from 'bricks/NSButton/NSButton';

const LowPriority = 1;

const ToolbarPlugin = () => {
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);

    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
        }
    }, []);

    useEffect(() => mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                $updateToolbar();
            });
        }),
        editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, _newEditor) => {
                $updateToolbar();
                return false;
            },
            LowPriority,
        ),
        editor.registerCommand(
            FORMAT_ELEMENT_COMMAND,
            (_payload, _newEditor) => {
                $updateToolbar();
                return false;
            },
            LowPriority,
        ),
        editor.registerCommand(
            INSERT_UNORDERED_LIST_COMMAND,
            () => {
                $insertList('bullet');
                return true;
            },
            LowPriority,
        ),
        editor.registerCommand(
            INSERT_ORDERED_LIST_COMMAND,
            () => {
                $insertList('number');
                return true;
            },
            LowPriority,
        ),
        editor.registerCommand(
            REMOVE_LIST_COMMAND,
            () => {
                $removeList();
                return true;
            },
            LowPriority,
        ),
    ), [editor, $updateToolbar]);

    return (
        <div
            ref={toolbarRef}
            className="NSRichTextEditor__toolbar d-flex px-2 py-2 border-bottom border-secondary align-middle w-100 h-100"
        >
            <div className="d-flex w-20 justify-content-between">
                <NSButton
                    outline
                    className={`text-white mr-1 border-0 ${isBold ? 'active' : ''}`}
                    icon={IconBold}
                    color="secondary"
                    size="sm"
                    callback={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                    includeTooltip
                    tooltip="Format bold"
                    id="rich-text-editor-bold-button"
                />
                <NSButton
                    outline
                    className={`text-white mr-1 border-0 ${isItalic ? 'active' : ''}`}
                    icon={IconItalic}
                    color="secondary"
                    size="sm"
                    callback={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                    tooltip="Format italic"
                    id="rich-text-editor-italic-button"
                />
                <NSButton
                    outline
                    className={`text-white mr-1 border-0 ${isUnderline ? 'active' : ''}`}
                    icon={IconUnderline}
                    color="secondary"
                    size="sm"
                    callback={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                    tooltip="Format underline"
                    id="rich-text-editor-underline-button"
                />
                <div className="NSRichTextEditor__toolbar-divider mx-2" />
                <NSButton
                    outline
                    className="text-white mr-1 border-0"
                    icon={IconList}
                    color="secondary"
                    size="sm"
                    callback={() => {
                        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                    }}
                    tooltip="Unordered list"
                    id="rich-text-editor-unordered-list-button"
                />
                <NSButton
                    outline
                    className="text-white mr-1 border-0"
                    icon={IconListNumbers}
                    color="secondary"
                    size="sm"
                    callback={() => {
                        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                    }}
                    tooltip="Ordered list"
                    id="rich-text-editor-ordered-list-button"
                />
            </div>
        </div>
    );
};

export default ToolbarPlugin;
